"use client";

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { usePrimaryHomeSurface } from "@/components/agentic-surface/useAgenticSurface";
import { resolveAdviserPersonaName } from "@/utils/adviserPersonaName";
import { resolveWorkspaceWelcomeConfig } from "@/utils/resolveWorkspaceWelcome";
import { resolveHomeBrandLogoUrl } from "@/components/home/homeBrandLogoUtils";
import { useColorSchemeWithFallback } from "@/context/ColorSchemeContext";
import * as AgenticV2API from "@/services/api/agentic-v2";
import {
  completeWorkspaceWelcome,
  configureWorkspaceWelcomeStorage,
  useWorkspaceWelcomeState,
} from "./workspaceWelcomeState";
import {
  completeOpenworkIntro,
  configureOpenworkDemoStorage,
  useOpenworkDemoState,
} from "@/components/review/openworkDemoState";
import { useOsDefaultLandingOpener } from "@/components/ui/os-layout/useOsDefaultLandingOpener";
import { WorkspaceWelcomeScreen } from "./WorkspaceWelcomeScreen";

/** Config-driven workspace welcome overlay for companies with welcome_screen enabled. */
export function WorkspaceWelcomeLayer() {
  const { user } = useAuth();
  const welcomeConfig = resolveWorkspaceWelcomeConfig(user);
  const { introCompleted: localIntroCompleted } = useWorkspaceWelcomeState();
  const openworkDemo = useOpenworkDemoState();
  const personaName = resolveAdviserPersonaName(user);
  const openDefaultLanding = useOsDefaultLandingOpener();
  const companyId = user?.company_id ?? "";
  const isOpenwork = companyId.toLowerCase() === "openworks";
  const { surfaceId: homeSurfaceId } = usePrimaryHomeSurface();

  useEffect(() => {
    if (isOpenwork) {
      configureOpenworkDemoStorage(user?.email ?? user?.id ?? null);
      return;
    }
    configureWorkspaceWelcomeStorage(user?.email ?? user?.id ?? null);
  }, [isOpenwork, user?.email, user?.id]);

  const { isDark } = useColorSchemeWithFallback();
  const brandingQuery = useQuery({
    queryKey: ["workspace-welcome-branding", companyId, homeSurfaceId],
    queryFn: () => AgenticV2API.getSurfaceSnapshot(homeSurfaceId),
    enabled: Boolean(welcomeConfig && companyId && homeSurfaceId),
    staleTime: 60_000,
  });

  if (!welcomeConfig) return null;
  if (isOpenwork && !openworkDemo.loaded) return null;

  const introCompleted = isOpenwork ? openworkDemo.introCompleted : localIntroCompleted;
  if (introCompleted) return null;

  const companyLogoUrl = resolveHomeBrandLogoUrl(
    brandingQuery.data?.surface_branding,
    isDark,
  );

  return (
    <WorkspaceWelcomeScreen
      config={welcomeConfig}
      personaName={personaName}
      companyLogoUrl={companyLogoUrl}
      aniHint={
        isOpenwork
          ? "Ani lives in the dock — click the logo any time if you want help."
          : null
      }
      onGetStarted={() => {
        if (isOpenwork) {
          void completeOpenworkIntro();
        } else {
          completeWorkspaceWelcome();
        }
        openDefaultLanding();
      }}
    />
  );
}
